import { useEffect, useState } from 'react'
import { FluentProvider } from '@fluentui/react-components'
import { librisDarkTheme, librisLightTheme } from './theme'
import { useRouter } from './lib/router'
import { useScrollProgress } from './lib/hooks'
import { SiteHeader } from './components/SiteHeader'
import { SiteFooter } from './components/SiteFooter'
import { Home } from './pages/Home'
import { Features } from './pages/Features'
import { Infrastructure } from './pages/Infrastructure'
import { Security } from './pages/Security'

const titles: Record<string, string> = {
  '/': 'Libris — Search the office, not the folder.',
  '/features': 'Features — Libris',
  '/infrastructure': 'Infrastructure — Libris',
  '/security': 'Security architecture — Libris',
}

const STORAGE_KEY = 'libris-theme'

function readInitialTheme(): boolean {
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'dark') return true
  if (stored === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export default function App() {
  const path = useRouter()
  const [dark, setDark] = useState(readInitialTheme)
  useScrollProgress()

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light'
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#080d12' : '#fcfcfa')
  }, [dark])

  useEffect(() => {
    document.title = titles[path] ?? titles['/']
  }, [path])

  return (
    // The site wrapper is nested rather than merged into FluentProvider: the
    // provider stamps its own background class on its root, which would win over
    // the paper/ink page colour defined by .site.
    <FluentProvider theme={dark ? librisDarkTheme : librisLightTheme} className={dark ? 'theme-dark' : undefined}>
      <div className="site">
        <a className="skip-link" href="#main">
          Skip to content
        </a>
        <SiteHeader path={path} dark={dark} setDark={setDark} />
        {path === '/features' ? (
          <Features />
        ) : path === '/infrastructure' ? (
          <Infrastructure />
        ) : path === '/security' ? (
          <Security />
        ) : (
          <Home />
        )}
        <SiteFooter />
      </div>
    </FluentProvider>
  )
}
